import React from "react";
import PropTypes from "prop-types";
import {
  getCasesByPhase,
  getCasesByPetitionerLawfirmInstitutionOutcome,
  getCasesByPatentOwnerLawfirmInstitutionOutcome,
  getCasesByAuthorJudgeInstitutionOutcome,
  getCasesByPanelJudgeInstitutionOutcome,
  getCasesByPetitionerLawfirmFWDOutcome,
  getCasesByPatentOwnerLawfirmFWDOutcome,
  getCasesByAuthorJudgeFWDOutcome,
  getCasesByPanelJudgeFWDOutcome,
  getCasesByTechCenter,
  getCasesByIndustry,
  getCasesByPetEntityType,
  getCasesByPOEntityType,
  getCasesByPOEntityTypeOverYears,
  getCasesByTypeOverYears,
  getCasesByInstitutionOutcomeOverYears,
  getCasesByDate,
  getCasesByClaimInstitutionOutcome,
  getCasesByClaimDecisionOutcome,
  getCasesByPetitionerLawfirmPerformance,
  getCasesByPatentOwnerLawfirmPerformance
} from "../../../../services/api-ptab";
import { SankeyChart, DonutChart, StackedBarChart, LineChart, makeWidthFlexible } from "@unifiedpatents/euclid-react";
import { nestedObjectsToGroupedSeries, objectToSeries, orderBy } from "@unifiedpatents/euclid-react/lib/helpers/preprocessors";
import * as Colors from "@unifiedpatents/euclid-react/lib/helpers/colors";
import { withChartData } from "../../../common/chart-data-hoc";
import { withChartConfig } from "../../../common/chart-config-hoc";

const COLOR_MAP_STATUS_PHASE = {
  "Adverse Judgment": Colors.blue200,
  "All Cases": Colors.grey200,
  "Final Written Decision": Colors.blue500,
  Instituted: Colors.blue400,
  Merits: Colors.grey400,
  "Not Instituted": Colors.grey400,
  Other: Colors.grey200,
  Pending: Colors.orange400,
  "Pending - Instituted": Colors.orange200,
  "Post-Institution": Colors.blue200,
  "Pre-Institution": Colors.blue300,
  Procedural: Colors.grey300,
  "Procedural Termination": Colors.grey500,
  Settlement: Colors.blue300,
  Total: Colors.grey200
};

const COLOR_MAP_INSTITUTION_OUTCOME = {
  "Not Instituted - Merits": Colors.grey400,
  "Not Instituted - Procedural": Colors.grey300,
  "Instituted - Granted": Colors.blue400
};

const COLOR_MAP_CLAIM_OUTCOME = {
  "All Claims Instituted": Colors.blue400,
  "All Claims Patentable": Colors.grey500,
  "All Claims Unpatentable": Colors.blue400,
  "Mixed Results": Colors.blue200,
  "No Claims Instituted": Colors.grey400
};

const COLOR_MAP_ENTITY_TYPE = {
  "OPERATING COMPANY": Colors.grey300,
  UNIVERSITY: Colors.grey400,
  "GOV/NGO/NON-PROFIT": Colors.grey500,
  "NPE (PATENT ASSERTION ENTITY)": Colors.blue400,
  "NPE (SMALL COMPANY)": Colors.blue300,
  "NPE (INDIVIDUAL)": Colors.blue200
};

const MARGIN_LEFT = { left: 160, right: 50, top: 50, bottom: 50 };
const MARGIN_MORE_LEFT = { left: 280, right: 50, top: 60, bottom: 50 };

const propValidation = { query: PropTypes.object };

export const CasesByPhaseChart = props => {
  const Chart = withChartConfig(withChartData(makeWidthFlexible(SankeyChart), getCasesByPhase, props.query));
  return <Chart title="Cases by Phase and Status" colorMap={{ ...COLOR_MAP_STATUS_PHASE, ...COLOR_MAP_CLAIM_OUTCOME }} totalIndex="Total" totalLabel="All Cases" height={500} {...props} />;
};
CasesByPhaseChart.propTypes = propValidation;

export const CasesByClaimOutcomeAtInstitutionChart = props => {
  const dataFormatter = data => orderBy(objectToSeries(data), ["No Claims Instituted", "Mixed Results", "All Claims Instituted"]);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(DonutChart), getCasesByClaimInstitutionOutcome, props.query));
  return <Chart title="Outcomes at Institution Decision" dataFormatter={dataFormatter} colorMap={COLOR_MAP_CLAIM_OUTCOME} height={500} scrollable={false} {...props} />;
};
CasesByClaimOutcomeAtInstitutionChart.propTypes = propValidation;

export const CasesByClaimOutcomeAtFWDChart = props => {
  const dataFormatter = data => orderBy(objectToSeries(data), ["All Claims Unpatentable", "Mixed Results", "All Claims Patentable"]);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(DonutChart), getCasesByClaimDecisionOutcome, props.query));
  return <Chart title="Outcomes at Final Written Decision" dataFormatter={dataFormatter} colorMap={COLOR_MAP_CLAIM_OUTCOME} height={500} scrollable={false} {...props} />;
};
CasesByClaimOutcomeAtFWDChart.propTypes = propValidation;

export const CasesByPetitionerLawfirmInstitutionOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByPetitionerLawfirmInstitutionOutcome, props.query), { show: "count", sort: "Instituted - Granted" });
  return (
    <Chart
      title="Institution Rates for Petitioners' Counsel"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP_INSTITUTION_OUTCOME}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_MORE_LEFT}
      legends={["Instituted - Granted", "Not Instituted - Merits", "Not Instituted - Procedural"]}
      {...props}
    />
  );
};
CasesByPetitionerLawfirmInstitutionOutcomeChart.propTypes = propValidation;

export const CasesByPatentOwnerLawfirmInstitutionOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByPatentOwnerLawfirmInstitutionOutcome, props.query), { show: "count", sort: "Not Instituted - Merits" });
  return (
    <Chart
      title="Institution Rates for Patent Owners' Counsel"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP_INSTITUTION_OUTCOME}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_MORE_LEFT}
      legends={["Instituted - Granted", "Not Instituted - Merits", "Not Instituted - Procedural"]}
      {...props}
    />
  );
};
CasesByPatentOwnerLawfirmInstitutionOutcomeChart.propTypes = propValidation;

export const CasesByAuthorJudgeInstitutionOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByAuthorJudgeInstitutionOutcome, props.query), { show: "count", sort: "__total__" });
  return (
    <Chart
      title="Institution Rates for Author Judge"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP_INSTITUTION_OUTCOME}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_LEFT}
      legends={["Instituted - Granted", "Not Instituted - Merits", "Not Instituted - Procedural"]}
      {...props}
    />
  );
};
CasesByAuthorJudgeInstitutionOutcomeChart.propTypes = propValidation;

export const CasesByPanelJudgeInstitutionOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByPanelJudgeInstitutionOutcome, props.query), { show: "count", sort: "__total__" });
  return (
    <Chart
      title="Institution Rates for Panel Judge"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP_INSTITUTION_OUTCOME}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_LEFT}
      legends={["Instituted - Granted", "Not Instituted - Merits", "Not Instituted - Procedural"]}
      {...props}
    />
  );
};
CasesByPanelJudgeInstitutionOutcomeChart.propTypes = propValidation;

export const CasesByPetitionerLawfirmPerformanceChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByPetitionerLawfirmPerformance, props.query), { show: "percentage", sort: "Instituted - Granted" });
  return (
    <Chart
      dataFormatter={dataFormatter}
      colorMap={{ ...COLOR_MAP_INSTITUTION_OUTCOME, ...COLOR_MAP_CLAIM_OUTCOME }}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_MORE_LEFT}
      legends={["Instituted - Granted", "Not Instituted - Merits", "All Claims Patentable"]}
      {...props}
    />
  );
};
CasesByPetitionerLawfirmPerformanceChart.propTypes = propValidation;

export const CasesByPatentOwnerLawfirmPerformanceChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByPatentOwnerLawfirmPerformance, props.query), { show: "percentage", sort: "Not Instituted - Merits" });
  return (
    <Chart
      dataFormatter={dataFormatter}
      colorMap={{ ...COLOR_MAP_INSTITUTION_OUTCOME, ...COLOR_MAP_CLAIM_OUTCOME }}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_MORE_LEFT}
      legends={["Instituted - Granted", "Not Instituted - Merits", "All Claims Patentable"]}
      {...props}
    />
  );
};
CasesByPatentOwnerLawfirmPerformanceChart.propTypes = propValidation;

export const CasesByPetitionerLawfirmFWDOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByPetitionerLawfirmFWDOutcome, props.query), { show: "count", sort: "All Claims Unpatentable" });
  return (
    <Chart
      dataFormatter={dataFormatter}
      title="Final Written Decision Rates for Petitioners' Counsel"
      colorMap={COLOR_MAP_CLAIM_OUTCOME}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_MORE_LEFT}
      legends={["All Claims Unpatentable", "Mixed Results", "All Claims Patentable"]}
      {...props}
    />
  );
};
CasesByPetitionerLawfirmFWDOutcomeChart.propTypes = propValidation;

export const CasesByPatentOwnerLawfirmFWDOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByPatentOwnerLawfirmFWDOutcome, props.query), { show: "count", sort: "All Claims Patentable" });
  return (
    <Chart
      dataFormatter={dataFormatter}
      title="Final Written Decision Rates for Patent Owners' Counsel"
      colorMap={COLOR_MAP_CLAIM_OUTCOME}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_MORE_LEFT}
      legends={["All Claims Unpatentable", "Mixed Results", "All Claims Patentable"]}
      {...props}
    />
  );
};
CasesByPatentOwnerLawfirmFWDOutcomeChart.propTypes = propValidation;

export const CasesByAuthorJudgeFWDOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByAuthorJudgeFWDOutcome, props.query), { show: "count", sort: "__total__" });
  return (
    <Chart
      title="Final Written Decision Rates for Author Judge"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP_CLAIM_OUTCOME}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_LEFT}
      legends={["All Claims Unpatentable", "Mixed Results", "All Claims Patentable"]}
      {...props}
    />
  );
};
CasesByAuthorJudgeFWDOutcomeChart.propTypes = propValidation;

export const CasesByPanelJudgeFWDOutcomeChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByPanelJudgeFWDOutcome, props.query), { show: "count", sort: "__total__" });
  return (
    <Chart
      title="Final Written Decision Rates for Panel Judge"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP_CLAIM_OUTCOME}
      orientation="vertical"
      showGridLines={false}
      height={750}
      margin={MARGIN_LEFT}
      legends={["All Claims Unpatentable", "Mixed Results", "All Claims Patentable"]}
      {...props}
    />
  );
};
CasesByPanelJudgeFWDOutcomeChart.propTypes = propValidation;

export const CasesByTechCenterChart = props => {
  const Chart = withChartConfig(withChartData(makeWidthFlexible(DonutChart), getCasesByTechCenter, props.query));
  return <Chart title="Cases by Tech Center" dataFormatter={objectToSeries} scrollable={false} {...props} />;
};
CasesByTechCenterChart.propTypes = propValidation;

export const CasesByIndustryChart = props => {
  const Chart = withChartConfig(withChartData(makeWidthFlexible(DonutChart), getCasesByIndustry, props.query));
  return <Chart title="Cases by Industry" dataFormatter={objectToSeries} scrollable={false} {...props} />;
};
CasesByIndustryChart.propTypes = propValidation;

export const CasesByPOEntityTypeChart = props => {
  const dataFormatter = data => orderBy(objectToSeries(data), ["NPE (PATENT ASSERTION ENTITY)", "NPE (SMALL COMPANY)", "NPE (INDIVIDUAL)", "OPERATING COMPANY", "UNIVERSITY", "GOV/NGO/NON-PROFIT"]);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(DonutChart), getCasesByPOEntityType, props.query));
  return <Chart title="Cases by P.O. Entity Type" dataFormatter={dataFormatter} colorMap={COLOR_MAP_ENTITY_TYPE} scrollable={false} {...props} />;
};
CasesByPOEntityTypeChart.propTypes = propValidation;

export const CasesByPetEntityTypeChart = props => {
  // TODO: fix name mapping and colors
  const dataFormatter = data => objectToSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(DonutChart), getCasesByPetEntityType, props.query));
  return <Chart title="Cases by Pet. Entity Type" dataFormatter={dataFormatter} colorMap={COLOR_MAP_ENTITY_TYPE} scrollable={false} {...props} />;
};
CasesByPetEntityTypeChart.propTypes = propValidation;

export const CasesByPOEntityTypeOverYearsChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByPOEntityTypeOverYears, props.query));
  return (
    <Chart
      title="NPE v.s. Non-NPE Patent Owners"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP_ENTITY_TYPE}
      legends={["NPE (PATENT ASSERTION ENTITY)", "NPE (SMALL COMPANY)", "NPE (INDIVIDUAL)", "OPERATING COMPANY", "UNIVERSITY", "GOV/NGO/NON-PROFIT"]}
      {...props}
    />
  );
};
CasesByPOEntityTypeOverYearsChart.propTypes = propValidation;

export const CasesByTypeOverYearsChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const colorMap = {
    DER: Colors.grey200,
    PGR: Colors.grey400,
    IPR: Colors.blue400,
    CBM: Colors.blue200
  };
  const Chart = withChartConfig(withChartData(makeWidthFlexible(StackedBarChart), getCasesByTypeOverYears, props.query));
  return <Chart title="Cases Per Year by Case Type" dataFormatter={dataFormatter} colorMap={colorMap} legends={["IPR", "CBM", "PGR", "DER"]} scrollable={false} {...props} />;
};
CasesByTypeOverYearsChart.propTypes = propValidation;

export const CasesByInstitutionOutcomeOverYearsChart = props => {
  const dataFormatter = data => nestedObjectsToGroupedSeries(data);
  const Chart = withChartConfig(withChartData(makeWidthFlexible(LineChart), getCasesByInstitutionOutcomeOverYears, props.query), { show: "percentage" });
  return (
    <Chart
      title="Institution Rate"
      dataFormatter={dataFormatter}
      colorMap={COLOR_MAP_INSTITUTION_OUTCOME}
      legends={["Instituted - Granted", "Not Instituted - Merits", "Not Instituted - Procedural"]}
      {...props}
    />
  );
};
CasesByInstitutionOutcomeOverYearsChart.propTypes = propValidation;

export const CasesByDateChart = props => {
  const Chart = withChartConfig(withChartData(makeWidthFlexible(LineChart), getCasesByDate, props.query));
  return <Chart title="Filed v.s. Terminated" markSize={2} tickFormatX={(v, i) => (i % 12 == 0 ? v.substring(0, 7) : "")} {...props} />;
};
CasesByDateChart.propTypes = propValidation;
